import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CreditCard, BadgeCheck, ShieldCheck } from "lucide-react";
import RefundPolicyDialog from "../components/RefundPolicyDialog";
import SuspensionCard from "../components/SuspensionCard";
import RenewalCard from "../components/RenewalCard";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import PricingTable from "../components/PricingTable";
import FounderUnlockBox from "../components/FounderUnlockBox";
import CheckoutModal from "../components/CheckoutModal";
import CheckoutResult from "../components/CheckoutResult";
import PendingTransferCard from "../components/PendingTransferCard";
import { Badge } from "../components/ui/badge";

const PLAN_LABEL = { free: "Starter (Gratis)", pro: "Pro", founder: "Fondatore 🏅", beta: "Beta Tester (Pro gratuito)" };
const PLAN_NAME = { pro: "Pro", founder: "Fondatore" };

export default function Billing() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [busyPlan, setBusyPlan] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [transfer, setTransfer] = useState(null);

  const load = async () => {
    const [s, c, t] = await Promise.all([api.get("/billing/status"), api.get("/billing/config"), api.get("/billing/bank-transfer")]);
    setStatus(s.data);
    setConfig(c.data);
    setTransfer(t.data.pending);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const priceFor = (plan, cycle, prices) => {
    if (plan === "pro") return cycle === "annual" ? prices.pro_annual : prices.pro_monthly;
    return cycle === "lifetime" ? prices.founder_lifetime : prices.founder_annual;
  };

  // Free plan activates directly; paid plans open the checkout modal.
  const activate = async (plan, cycle) => {
    if (plan === "free") {
      setBusyPlan("free");
      try {
        await api.post("/billing/activate", { plan, cycle });
        toast.success("Sei passato al Piano Starter");
        await load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Attivazione non riuscita"); }
      setBusyPlan(null);
      return;
    }
    const prices = config?.prices || {};
    setCheckout({ plan, cycle, price: priceFor(plan, cycle, prices), label: PLAN_NAME[plan] });
  };

  const confirmCheckout = async (promoCode, method) => {
    setConfirming(true);
    const body = { plan: checkout.plan, cycle: checkout.cycle, promo_code: promoCode || "" };
    try {
      if (method === "bonifico") {
        await api.post("/billing/bank-transfer", body);
        toast.success("Richiesta registrata: esegui il bonifico con la causale indicata");
        setCheckout(null);
        await load();
      } else {
        const { data } = await api.post("/billing/checkout", { ...body, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pagamento non avviato. Riprova.");
    }
    setConfirming(false);
  };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const plan = status.plan;
  return (
    <div data-testid="billing-page">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><CreditCard style={{ width: 20, height: 20 }} className="text-primary" /></div>
        <div>
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">Abbonamento e Prezzi</h1>
          <p className="text-sm text-slate-500">Gestisci il tuo piano. Pagamenti sicuri con carta (Stripe) o bonifico.</p>
        </div>
      </div>

      <div data-testid="billing-current-plan" className="rounded-xl border border-slate-200/80 bg-card p-5 shadow-sm mb-8 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <BadgeCheck style={{ width: 22, height: 22 }} className={plan === "free" ? "text-slate-400" : "text-emerald-500"} />
          <div>
            <p className="text-sm text-slate-500">Piano attuale</p>
            <p className="font-bold text-slate-900 flex items-center gap-2">
              {PLAN_LABEL[plan]}
              {status.founder_member && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Membro Fondatore</Badge>}
            </p>
          </div>
        </div>
        <div className="text-right text-sm">
          {plan === "beta" ? (
            <p data-testid="billing-beta-count" className="text-slate-500">{status.beta.quotes_created}/{status.beta.limit} preventivi Beta usati</p>
          ) : status.beta?.ended && plan === "free" ? (
            <p data-testid="billing-beta-ended" className="text-violet-700 font-medium">Fase Beta terminata</p>
          ) : plan === "free" ? (
            <p data-testid="billing-quotes-remaining" className="text-slate-500">{status.quotes_remaining} preventivi rimasti su {status.limits.max_quotes} questo mese</p>
          ) : (
            <p className="text-slate-500" data-testid="billing-expiry">{status.subscription_status === "suspended" ? "In pausa" : status.cancel_at_period_end ? `Attivo fino al ${fmtDate(status.scadenza_abbonamento)} (rinnovo disdetto)` : status.billing_cycle === "lifetime" ? "Accesso a vita" : status.scadenza_abbonamento ? `Rinnovo il ${fmtDate(status.scadenza_abbonamento)}` : "Attivo"}</p>
          )}
        </div>
      </div>

      <CheckoutResult onActivated={load} />
      {status.payment_failed_at && (
        <div data-testid="billing-payment-failed" className="mb-6 rounded-xl border border-red-200 bg-red-50 px-5 py-4">
          <p className="font-bold text-red-800">Rinnovo non riuscito</p>
          <p className="text-sm text-red-700 mt-1">L'ultimo addebito automatico è stato rifiutato. Aggiorna il metodo di pagamento dal link ricevuto via email da Stripe per non perdere il piano.</p>
        </div>
      )}
      <PendingTransferCard transfer={transfer} onChanged={load} />
      {status.beta?.ended && plan === "free" && (
        <div data-testid="billing-beta-ended-banner" className="mb-6 rounded-xl border border-violet-200 bg-violet-50 px-5 py-4">
          <p className="font-bold text-violet-900">Grazie per la tua recensione! La Fase Beta è terminata.</p>
          <p className="text-sm text-violet-800 mt-1">Per continuare a creare preventivi scegli il piano Fondatore, Mensile o Annuale. I preventivi già creati restano consultabili.</p>
        </div>
      )}

      <RenewalCard status={status} onChanged={load} />
      <SuspensionCard status={status} onChanged={load} />

      {config?.promo_banner?.enabled && (
        <div data-testid="billing-promo-banner" className="mb-6 rounded-xl border border-amber-300 bg-amber-400 text-white px-5 py-4 shadow-sm">
          <p className="font-bold">{config.promo_banner.title || "Offerta speciale"}</p>
          {config.promo_banner.text && <p className="text-sm text-amber-50 mt-0.5">{config.promo_banner.text}</p>}
          {config.promo_banner.code && (
            <span className="inline-block mt-2 px-3 py-1 rounded-md bg-white/20 font-mono font-bold tracking-wider">{config.promo_banner.code}</span>
          )}
        </div>
      )}

      {config?.founder_private && !status.founder_unlocked && <FounderUnlockBox onUnlocked={load} />}

      <PricingTable onSelect={activate} currentPlan={plan} config={config} busyPlan={busyPlan} founderUnlocked={status.founder_unlocked} />

      <div className="mt-6 flex justify-center">
        <button type="button" data-testid="refund-policy-link" onClick={() => setPolicyOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-primary underline underline-offset-4">
          <ShieldCheck style={{ width: 15, height: 15 }} /> Garanzia e Condizioni di Rimborso (Totale o Parziale)
        </button>
      </div>
      <RefundPolicyDialog open={policyOpen} onOpenChange={setPolicyOpen} />

      {checkout && (
        <CheckoutModal
          open={!!checkout}
          onOpenChange={(v) => { if (!v) setCheckout(null); }}
          plan={checkout.plan}
          cycle={checkout.cycle}
          basePrice={checkout.price}
          planLabel={checkout.label}
          ownerBank={status?.owner_bank}
          cardEnabled={!!status.card_payments_enabled}
          onConfirm={confirmCheckout}
          confirming={confirming}
        />
      )}
    </div>
  );
}
